import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { APP } from '@/constants/testIds';
import { toast } from 'sonner';
import { Plus, Trash2, Calendar } from 'lucide-react';

const TRIGGERS = ['new_device', 'old_ip_reconnect', 'failed_logins', 'external_access', 'suspicious_traffic'];

const Settings = () => {
  const [rules, setRules] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [ruleName, setRuleName] = useState('');
  const [ruleTrigger, setRuleTrigger] = useState('new_device');
  const [emailCh, setEmailCh] = useState(true);
  const [smsCh, setSmsCh] = useState(false);
  const [schedNet, setSchedNet] = useState('');
  const [schedFreq, setSchedFreq] = useState('weekly');
  const [newLocName, setNewLocName] = useState('');
  const [newLocAddr, setNewLocAddr] = useState('');

  const load = async () => {
    const [r, s, n] = await Promise.all([
      api.get('/settings/alert-rules').then((x) => x.data),
      api.get('/schedules').then((x) => x.data),
      api.get('/networks').then((x) => x.data),
    ]);
    setRules(r); setSchedules(s); setNetworks(n);
    if (n[0]) setSchedNet(n[0].network_id);
  };
  useEffect(() => { load(); }, []);

  const createRule = async (e) => {
    e.preventDefault();
    if (!ruleName.trim()) { toast.error('Rule name required'); return; }
    const channels = [];
    if (emailCh) channels.push('email');
    if (smsCh) channels.push('sms');
    await api.post('/settings/alert-rules', { name: ruleName, trigger: ruleTrigger, channels });
    setRuleName(''); toast.success('Rule created'); load();
  };
  const deleteRule = async (id) => { await api.delete(`/settings/alert-rules/${id}`); load(); };

  const createSchedule = async () => {
    if (!schedNet) { toast.error('Select a network'); return; }
    await api.post('/schedules', { network_id: schedNet, frequency: schedFreq });
    toast.success('Schedule created'); load();
  };
  const deleteSchedule = async (id) => { await api.delete(`/schedules/${id}`); load(); };

  const addNetwork = async (e) => {
    e.preventDefault();
    if (!newLocName.trim()) return;
    await api.post('/networks', { name: newLocName, location: newLocAddr });
    setNewLocName(''); setNewLocAddr(''); toast.success('Location added'); load();
  };
  const deleteNetwork = async (id) => { await api.delete(`/networks/${id}`); load(); };

  return (
    <div className="space-y-10">
      <header>
        <div className="label-tag mb-2">// Settings</div>
        <h1 className="text-4xl md:text-5xl font-light tracking-tight">Configuration</h1>
        <p className="text-[color:var(--text-secondary)] mt-2 max-w-2xl">
          Alert rules, scan schedules, and business location inventory.
        </p>
      </header>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[color:var(--border)]">
        {/* Alert rules */}
        <div className="bg-[color:var(--bg-base)] p-6">
          <div className="label-tag mb-3">// Alert Rules</div>
          <form onSubmit={createRule} className="space-y-4 mb-6" data-testid={APP.ruleNewBtn}>
            <div className="grid grid-cols-2 gap-3">
              <input data-testid={APP.ruleNameInput} value={ruleName} onChange={(e) => setRuleName(e.target.value)} placeholder="Rule name" className="input-base col-span-2" />
              <select value={ruleTrigger} onChange={(e) => setRuleTrigger(e.target.value)} className="input-base col-span-2">
                {TRIGGERS.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
              </select>
              <label className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[color:var(--text-secondary)]">
                <input type="checkbox" checked={emailCh} onChange={(e) => setEmailCh(e.target.checked)} /> Email
              </label>
              <label className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[color:var(--text-secondary)]">
                <input type="checkbox" checked={smsCh} onChange={(e) => setSmsCh(e.target.checked)} /> SMS
              </label>
            </div>
            <button data-testid={APP.ruleCreateBtn} type="submit" className="btn-primary !py-2 !text-[11px]"><Plus size={12} /> Create rule</button>
          </form>

          <ul className="space-y-2">
            {rules.length === 0 ? <Empty>No rules yet.</Empty> : rules.map((r) => (
              <li key={r.rule_id} className="flex items-center gap-3 panel-soft p-3">
                <div className="flex-1">
                  <div className="text-sm">{r.name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">
                    {r.trigger.replace(/_/g, ' ')} · {(r.channels || []).join(', ')}
                  </div>
                </div>
                <button onClick={() => deleteRule(r.rule_id)} className="p-1.5 border border-[color:var(--border)] hover:border-[color:var(--status-critical)] hover:text-[color:var(--status-critical)]">
                  <Trash2 size={12} />
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Schedules */}
        <div className="bg-[color:var(--bg-base)] p-6">
          <div className="label-tag mb-3">// Scan Schedules</div>
          <div className="space-y-4 mb-6">
            <select value={schedNet} onChange={(e) => setSchedNet(e.target.value)} className="input-base">
              <option value="">Select network…</option>
              {networks.map((n) => <option key={n.network_id} value={n.network_id}>{n.name}</option>)}
            </select>
            <select value={schedFreq} onChange={(e) => setSchedFreq(e.target.value)} className="input-base">
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
            <button data-testid={APP.scheduleCreateBtn} onClick={createSchedule} className="btn-primary !py-2 !text-[11px]"><Calendar size={12} /> Schedule</button>
          </div>
          <ul className="space-y-2">
            {schedules.length === 0 ? <Empty>No schedules.</Empty> : schedules.map((s) => (
              <li key={s.schedule_id} className="flex items-center gap-3 panel-soft p-3">
                <div className="flex-1">
                  <div className="text-sm">{networks.find((n) => n.network_id === s.network_id)?.name || s.network_id}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">
                    {s.frequency} · next {new Date(s.next_run).toLocaleDateString()}
                  </div>
                </div>
                <button onClick={() => deleteSchedule(s.schedule_id)} className="p-1.5 border border-[color:var(--border)] hover:border-[color:var(--status-critical)] hover:text-[color:var(--status-critical)]">
                  <Trash2 size={12} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Business locations / networks */}
      <section className="panel p-6">
        <div className="label-tag mb-3">// Business Locations</div>
        <form onSubmit={addNetwork} className="flex flex-col md:flex-row gap-3 mb-5">
          <input value={newLocName} onChange={(e) => setNewLocName(e.target.value)} placeholder="Location name (e.g., HQ Office)" className="input-base flex-1" />
          <input value={newLocAddr} onChange={(e) => setNewLocAddr(e.target.value)} placeholder="Address / floor" className="input-base flex-1" />
          <button type="submit" className="btn-primary !py-2 !text-[11px]"><Plus size={12} /> Add location</button>
        </form>
        <ul className="space-y-2">
          {networks.length === 0 ? <Empty>No locations yet.</Empty> : networks.map((n) => (
            <li key={n.network_id} className="flex items-center gap-3 panel-soft p-3">
              <div className="flex-1">
                <div className="text-sm">{n.name}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">
                  {n.location} · {n.cidr}
                </div>
              </div>
              <button onClick={() => deleteNetwork(n.network_id)} className="p-1.5 border border-[color:var(--border)] hover:border-[color:var(--status-critical)] hover:text-[color:var(--status-critical)]">
                <Trash2 size={12} />
              </button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
};
const Empty = ({ children }) => <div className="font-mono text-xs text-[color:var(--text-secondary)] py-4">{children}</div>;
export default Settings;
